import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { collection, getDocs, doc, getDoc, updateDoc, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
  LuFileSpreadsheet,
  LuListFilter,
  LuMousePointerClick,
  LuSearch,
  LuUserCheck,
  LuUserMinus,
  LuUserPlus,
  LuUserRoundX,
  LuUsers,
  LuX,
} from 'react-icons/lu';
import { db } from '../../config/firebaseConfig';
import {
  dedupeCenters,
  resolveCanonicalCenterId,
} from '../../utils/centerDedupe';
import {
  archiveAssignment,
  restoreAssignment,
  isArchivedAssignment,
  formatUnassignedDate,
} from '../../utils/assignmentHistory';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useTranslation } from '../../i18n/LanguageContext';
import useMediaQuery from '../../hooks/useMediaQuery';
// Only global theme variables are left in this file; importing it keeps their place in the cascade
import './WorkersModal.module.css';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { GLASS_FIELD } from '../../components/ui/glass';
import FilterSheet from '../../components/data/FilterSheet';
import EmployeeFileUploadModal from './EmployeeFileUploadModal';
import WorkerFormModal from './workers/WorkerFormModal';
import WorkerDetailCard, { WorkerActions } from './workers/WorkerDetailCard';
import WorkerList, { WORKER_FILTER_KEYS, workerFilterFields } from './workers/WorkerList';
import WorkerRemoveDialog from './workers/WorkerRemoveDialog';
import { DEFAULT_TEST_TARIFF, MAIN_ADMIN_EMAIL, MANAGER_ROLES, centerDedupeLabel, formatAssignedDate, summarizeWorker } from './workers/workerOptions';

const EMPTY_FILTERS = {
  name: '',
  surname: '',
  role: '',
  rate: '',
  center: '',
  info: '',
  assignedAt: '',
  status: ''
};

const lower = (value) => String(value || '').toLocaleLowerCase('tr-TR');

/**
 * "Çalışanlar" of a project. A worker has one row per (center, role) in both
 * Projects/{id}.employees and Employees/{id}.projects; the list groups them per
 * person. Add / edit / file upload / (main admin) remove for good; leaving a
 * center archives its rows (unassignedAt) instead of deleting them.
 */
const WorkersModal = ({ project, onClose }) => {
  const { t, language, locale } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const { userEmail } = useUserProfile();
  const isMainAdmin =
    String(userEmail || '').trim().toLowerCase() === MAIN_ADMIN_EMAIL;
  const wide = useMediaQuery('(min-width: 768px)');
  const docked = useMediaQuery('(min-width: 1180px)');
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddWorkerModal, setShowAddWorkerModal] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedRole, setSelectedRole] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [centers, setCenters] = useState([]);
  const [selectedCenters, setSelectedCenters] = useState([]);
  const [centerTariffs, setCenterTariffs] = useState({});
  const [selectedInfo, setSelectedInfo] = useState('');
  const [selectedWorkerType, setSelectedWorkerType] = useState('');
  const [selectedBlindStatus, setSelectedBlindStatus] = useState('');
  const [hasManagerWarning, setHasManagerWarning] = useState(false);
  const [isOnlyHourly, setIsOnlyHourly] = useState(false);

  // Maps every duplicate Centers document to the one option shown for it, so
  // checks across centers treat the copies as a single center.
  const canonicalCenterIdById = useMemo(
    () => dedupeCenters(centers, centerDedupeLabel).canonicalIdById,
    [centers]
  );

  // Check if this is a test project
  const isTestProject = project?.isTestProject === true || project?.isTestProject === 'true';
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [showEditWorkerModal, setShowEditWorkerModal] = useState(false);
  const [workerToEdit, setWorkerToEdit] = useState(null);
  // Centers the worker has left. Their rows stay in Firestore with an
  // `unassignedAt` stamp, so the work done there is not lost.
  const [archivedCenters, setArchivedCenters] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('active');
  const [showEmployeeFileUploadModal, setShowEmployeeFileUploadModal] = useState(false);
  const [selectedEmployeeForUpload, setSelectedEmployeeForUpload] = useState(null);
  // Seçili çalışan id'si; ayrıntı her zaman güncel (ve filtrelenmemiş) satırlardan kurulur
  const [selectedRowId, setSelectedRowId] = useState(null);
  // Kalıcı silme onayı bekleyen çalışan (yalnızca ana admin)
  const [workerToRemove, setWorkerToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const toastText = (key, vars) => t(`projectManagement.workers.toast.${key}`, vars);

  // Excel ve filtre için (eskisi gibi tr-TR)
  const formatDate = (timestamp) => formatAssignedDate(timestamp, 'tr-TR');

  const fetchProjectWorkers = async () => {
    try {
      if (!project || !project.id) {
        setLoading(false);
        return;
      }

      const projectDoc = await getDoc(doc(db, 'Projects', project.id));
      const projectData = projectDoc.data();

      if (!projectData || !projectData.employees) {
        setWorkers([]);
        setLoading(false);
        return;
      }

      const workerDetailsPromises = projectData.employees.map(async (employee) => {
        if (typeof employee === 'string') {
          const employeeDoc = await getDoc(doc(db, 'Employees', employee));
          if (employeeDoc.exists()) {
            return {
              id: employeeDoc.id,
              ...employeeDoc.data(),
              assignedAt: projectData.employees.find(e => e.id === employee)?.assignedAt
            };
          }
          return undefined;
        }
        return employee;
      });

      const workerDetails = await Promise.all(workerDetailsPromises);
      setWorkers(workerDetails.filter(Boolean));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching project workers:', error);
      toast.error(tRef.current('projectManagement.workers.toast.loadError'));
      setLoading(false);
    }
  };

  const fetchTariffs = async () => {
    try {
      if (!project || !project.id) {
        setTariffs([]);
        setIsOnlyHourly(false);
        return;
      }

      const tariffsRef = collection(db, 'Tariffs');
      const querySnapshot = await getDocs(query(tariffsRef, where('project', '==', project.id)));
      const tariffsData = querySnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      // Hem FTE hem saat var mı kontrol et
      const hasFTE = tariffsData.some(tariff => tariff.type?.toUpperCase() === 'FTE');
      const hasHourly = tariffsData.some(tariff => tariff.type?.toUpperCase() !== 'FTE');

      // Tüm tarifeleri sakla - filtrelemeyi dropdown seviyesinde yapacağız
      setTariffs(tariffsData);

      // Sadece saat varsa flag'i set et (tüm merkezler için otomatik seçim yapılacak)
      setIsOnlyHourly(!hasFTE && hasHourly);
    } catch (error) {
      console.error('Error fetching tariffs:', error);
      toast.error(tRef.current('projectManagement.workers.toast.tariffsError'));
      setTariffs([]);
      setIsOnlyHourly(false);
    }
  };

  const fetchCenters = async () => {
    try {
      // Fetch all centers from Centers collection
      const centersRef = collection(db, 'Centers');
      const querySnapshot = await getDocs(centersRef);
      const centersData = querySnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      // Also include centers that are used in the current project but might be deleted from Centers collection
      const centerMap = new Map();

      // Add all centers from Centers collection
      centersData.forEach(center => {
        centerMap.set(center.id, center);
      });

      // Add centers from current project's employees (if they don't exist in Centers collection)
      if (project && project.id) {
        try {
          const projectDoc = await getDoc(doc(db, 'Projects', project.id));
          const projectData = projectDoc.data();

          if (projectData && projectData.employees) {
            projectData.employees.forEach(emp => {
              if (emp.centerId && !centerMap.has(emp.centerId)) {
                // This center is deleted from Centers collection but still exists in project
                centerMap.set(emp.centerId, {
                  id: emp.centerId,
                  Adı: emp.centerName || 'Silinmiş Merkez',
                  Bilgi: '(Merkez silinmiş - sadece görüntüleme)',
                  Durum: 'İnaktif',
                  İl: '',
                  İlçe: '',
                  isDeleted: true // Flag to indicate this center is deleted
                });
              }
            });
          }
        } catch (error) {
          console.error('Error fetching project data for centers:', error);
        }
      }

      // Convert map to array and sort
      const allCenters = Array.from(centerMap.values()).sort((a, b) => {
        // Put deleted centers at the end
        if (a.isDeleted && !b.isDeleted) return 1;
        if (!a.isDeleted && b.isDeleted) return -1;
        return (a.Adı || '').localeCompare(b.Adı || '', 'tr');
      });

      setCenters(allCenters);
    } catch (error) {
      console.error('Error fetching centers:', error);
      toast.error(tRef.current('projectManagement.workers.toast.centersError'));
    }
  };

  useEffect(() => {
    fetchProjectWorkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const employeesRef = collection(db, 'Employees');
        const querySnapshot = await getDocs(employeesRef);
        const employees = querySnapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        setAvailableEmployees(employees);

        await fetchTariffs();
        await fetchCenters();
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(tRef.current('projectManagement.workers.toast.dataError'));
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for existing manager whenever role or centers change
  useEffect(() => {
    // roles artık array, herhangi biri manager rolü mü kontrol et
    const hasManagerRole = Array.isArray(selectedRole) && selectedRole.some(role => MANAGER_ROLES.includes(role));

    if (!hasManagerRole || selectedCenters.length === 0) {
      setHasManagerWarning(false);
      return;
    }

    // Duplicate Centers documents are one center as far as the user is
    // concerned, so an existing manager saved against another copy of the same
    // center still counts as a clash.
    const selectedCanonicalIds = new Set(
      selectedCenters.map(id => resolveCanonicalCenterId(canonicalCenterIdById, id))
    );

    // Düzenlenen çalışanın kendi satırları ve ayrıldığı merkezlerdeki eski
    // atamalar çakışma sayılmaz (yoksa bir sorumlu kendi kaydını düzenleyemiyordu).
    const existingManager = workers.find(worker =>
      MANAGER_ROLES.includes(worker.role) &&
      !isArchivedAssignment(worker) &&
      !(showEditWorkerModal && workerToEdit && worker.id === workerToEdit.id) &&
      selectedCanonicalIds.has(resolveCanonicalCenterId(canonicalCenterIdById, worker.centerId))
    );

    if (existingManager) {
      setHasManagerWarning(true);
      toast.error(tRef.current('projectManagement.workers.toast.managerExists'));
    } else {
      setHasManagerWarning(false);
    }
  }, [selectedRole, selectedCenters, workers, canonicalCenterIdById, showEditWorkerModal, workerToEdit]);

  const resetForm = () => {
    setSelectedEmployee('');
    setSelectedRole([]);
    setSelectedCenters([]);
    setArchivedCenters([]);
    setCenterTariffs({});
    setSelectedInfo('');
    setSelectedWorkerType('');
    setSelectedBlindStatus('');
    setSelectedStatus('active');
  };

  const handleAddWorker = async () => {
    if (!selectedEmployee || selectedRole.length === 0 || selectedCenters.length === 0) {
      toast.error(toastText('required'));
      return;
    }

    // Default test tariff for test projects
    const defaultTestTariff = DEFAULT_TEST_TARIFF;

    // Eğer sadece saatlik tarife varsa, otomatik olarak ilk tarifeyi kullan
    let effectiveCenterTariffs = centerTariffs;
    let useDefaultTestTariff = false;

    if (isTestProject) {
      // For test projects, use default tariff if none selected
      useDefaultTestTariff = true;
      effectiveCenterTariffs = {};
      selectedCenters.forEach(centerId => {
        effectiveCenterTariffs[centerId] = centerTariffs[centerId] || 'test-tariff-default';
      });
    } else if (isOnlyHourly && tariffs.length > 0) {
      const hourlyTariff = tariffs[0];
      effectiveCenterTariffs = {};
      selectedCenters.forEach(centerId => {
        effectiveCenterTariffs[centerId] = hourlyTariff.id;
      });
    } else {
      // No tariff required — rate managed on a separate page
      effectiveCenterTariffs = {};
    }

    if (hasManagerWarning) {
      toast.error(toastText('managerExists'));
      return;
    }

    setLoading(true);
    try {
      const projectRef = doc(db, 'Projects', project.id);
      const employeeRef = doc(db, 'Employees', selectedEmployee);

      // Get current data for both documents
      const projectDoc = await getDoc(projectRef);
      const employeeDoc = await getDoc(employeeRef);
      const currentProjectData = projectDoc.data();
      const currentEmployeeData = employeeDoc.data();

      // Check if employee is already assigned to project
      const currentEmployees = currentProjectData.employees || [];
      const isAlreadyAssigned = currentEmployees.some(e => e.id === selectedEmployee);

      if (isAlreadyAssigned) {
        toast.error(toastText('alreadyAssigned'));
        setLoading(false);
        return;
      }

      // Prepare new employee data for project - create separate entries for each center AND each role
      const newEmployeeEntries = [];
      for (const role of selectedRole) {
        for (const centerId of selectedCenters) {
          let selectedTariff;
          if (useDefaultTestTariff && effectiveCenterTariffs[centerId] === 'test-tariff-default') {
            selectedTariff = defaultTestTariff;
          } else {
            selectedTariff = tariffs.find(tariff => tariff.id === effectiveCenterTariffs[centerId]);
          }
          newEmployeeEntries.push({
            id: selectedEmployee,
            name: currentEmployeeData.eName,
            surname: currentEmployeeData.eSurname,
            rate: selectedTariff ? {
              id: selectedTariff.id,
              type: selectedTariff.type,
              value: selectedTariff.value,
              price: selectedTariff.price,
              currency: selectedTariff.currency
            } : null,
            role: role,
            centerId: centerId,
            centerName: centers.find(c => c.id === centerId)?.Adı,
            assignedAt: new Date(),
            info: selectedInfo,
            workerType: selectedWorkerType,
            blindStatus: selectedBlindStatus,
            status: 'active'
          });
        }
      }

      const updatedEmployees = [...currentEmployees, ...newEmployeeEntries];

      // Prepare new project data for employee - create separate entries for each center AND each role
      const employeeProjects = currentEmployeeData.projects || [];
      const newProjectEntries = [];
      for (const role of selectedRole) {
        for (const centerId of selectedCenters) {
          let selectedTariff;
          if (useDefaultTestTariff && effectiveCenterTariffs[centerId] === 'test-tariff-default') {
            selectedTariff = defaultTestTariff;
          } else {
            selectedTariff = tariffs.find(tariff => tariff.id === effectiveCenterTariffs[centerId]);
          }
          newProjectEntries.push({
            id: project.id,
            titleShort: project.titleShort,
            titleLong: project.titleLong,
            projectDate: project.projectDate,
            rate: selectedTariff ? {
              id: selectedTariff.id,
              type: selectedTariff.type,
              value: selectedTariff.value,
              price: selectedTariff.price,
              currency: selectedTariff.currency
            } : null,
            role: role,
            centerId: centerId,
            centerName: centers.find(c => c.id === centerId)?.Adı,
            assignedAt: new Date(),
            workerType: selectedWorkerType,
            blindStatus: selectedBlindStatus,
            status: 'active'
          });
        }
      }

      const updatedProjects = [...employeeProjects, ...newProjectEntries];

      // Update both documents
      await updateDoc(projectRef, {
        employees: updatedEmployees
      });

      await updateDoc(employeeRef, {
        projects: updatedProjects
      });

      toast.success(toastText('added'));
      setShowAddWorkerModal(false);
      // Geniş ekranda yeni çalışan yan panelde seçili gelir
      if (docked) setSelectedRowId(selectedEmployee);
      await fetchProjectWorkers(); // Refresh the workers list

      // Reset form
      resetForm();

    } catch (error) {
      console.error('Error adding worker:', error);
      toast.error(toastText('addError'));
    }
    setLoading(false);
  };

  const handleEditWorker = async () => {
    // A worker with only archived centers is a valid state: they left every
    // center but their past work is still on record.
    if (selectedRole.length === 0 || (selectedCenters.length === 0 && archivedCenters.length === 0)) {
      toast.error(toastText('required'));
      return;
    }

    // Default test tariff for test projects
    const defaultTestTariff = DEFAULT_TEST_TARIFF;

    // Eğer sadece saatlik tarife varsa, otomatik olarak ilk tarifeyi kullan
    let effectiveCenterTariffs = centerTariffs;
    let useDefaultTestTariff = false;

    if (isTestProject) {
      // For test projects, use default tariff if none selected
      useDefaultTestTariff = true;
      effectiveCenterTariffs = {};
      selectedCenters.forEach(centerId => {
        effectiveCenterTariffs[centerId] = centerTariffs[centerId] || 'test-tariff-default';
      });
    } else if (isOnlyHourly && tariffs.length > 0) {
      const hourlyTariff = tariffs[0];
      effectiveCenterTariffs = {};
      selectedCenters.forEach(centerId => {
        effectiveCenterTariffs[centerId] = hourlyTariff.id;
      });
    } else {
      // No tariff required — rate managed on a separate page
      effectiveCenterTariffs = {};
    }

    if (hasManagerWarning) {
      toast.error(toastText('managerExists'));
      return;
    }

    setLoading(true);
    try {
      const projectRef = doc(db, 'Projects', project.id);
      const employeeRef = doc(db, 'Employees', workerToEdit.id);

      // Get current data for both documents
      const projectDoc = await getDoc(projectRef);
      const employeeDoc = await getDoc(employeeRef);
      const currentProjectData = projectDoc.data();
      const currentEmployeeData = employeeDoc.data();

      // Rows for this worker in the centers the modal originally loaded. Rows in
      // any other center belong to an edit we must not touch.
      const originalCenterIds = workerToEdit.centers.map(c => c.centerId);
      const isEditedEmployeeRow = emp =>
        emp.id === workerToEdit.id && originalCenterIds.includes(emp.centerId);

      const untouchedEmployees = currentProjectData.employees.filter(emp => !isEditedEmployeeRow(emp));
      const editedEmployeeRows = currentProjectData.employees.filter(isEditedEmployeeRow);

      // Preserve existing per-center rates so edits don't wipe rates managed on the FTE Yönetimi page
      const existingRateByCenter = {};
      (workerToEdit.centers || []).forEach(c => {
        if (c.centerId && c.rate) existingRateByCenter[c.centerId] = c.rate;
      });

      const unassignedAtIso = new Date().toISOString();
      const archivedCenterIds = new Set(archivedCenters.map(c => c.centerId));

      // Centers the worker left: keep the original row untouched apart from the
      // ayrılma damgası, so uploaded files and rate history survive.
      const archivedEmployeeRows = editedEmployeeRows
        .filter(emp => archivedCenterIds.has(emp.centerId))
        .map(emp => archiveAssignment(emp, unassignedAtIso));

      // The row this (center, role) pair had before the edit — reused so fields
      // the modal does not manage (files, smoId, centerDeletedAt) are carried
      // over instead of being dropped.
      const previousRowFor = (centerId, role) =>
        editedEmployeeRows.find(emp => emp.centerId === centerId && emp.role === role) ||
        editedEmployeeRows.find(emp => emp.centerId === centerId);

      // Add new entries for each selected center AND each role
      const newEmployeeEntries = [];
      for (const role of selectedRole) {
        for (const centerId of selectedCenters) {
          let selectedTariff;
          if (useDefaultTestTariff && effectiveCenterTariffs[centerId] === 'test-tariff-default') {
            selectedTariff = defaultTestTariff;
          } else {
            selectedTariff = tariffs.find(tariff => tariff.id === effectiveCenterTariffs[centerId]);
          }
          const rate = selectedTariff ? {
            id: selectedTariff.id,
            type: selectedTariff.type,
            value: selectedTariff.value,
            price: selectedTariff.price,
            currency: selectedTariff.currency
          } : (existingRateByCenter[centerId] || null);
          // restoreAssignment: re-selecting an archived center reactivates the
          // original row (and its files) instead of creating an empty one.
          const previousRow = restoreAssignment(previousRowFor(centerId, role)) || {};
          newEmployeeEntries.push({
            ...previousRow,
            id: workerToEdit.id,
            name: workerToEdit.name || workerToEdit.eName,
            surname: workerToEdit.surname || workerToEdit.eSurname,
            rate,
            role: role,
            centerId: centerId,
            centerName: centers.find(c => c.id === centerId)?.Adı,
            assignedAt: workerToEdit.assignedAt || new Date(),
            info: selectedInfo,
            workerType: selectedWorkerType,
            blindStatus: selectedBlindStatus,
            status: selectedStatus
          });
        }
      }

      const finalEmployees = [...untouchedEmployees, ...archivedEmployeeRows, ...newEmployeeEntries];

      console.log('🔄 Updating worker in project:', {
        projectId: project.id,
        projectTitle: project.titleShort || project.titleLong,
        workerId: workerToEdit.id,
        workerName: `${workerToEdit.name || workerToEdit.eName} ${workerToEdit.surname || workerToEdit.eSurname}`,
        selectedWorkerType,
        selectedBlindStatus,
        newEmployeeEntries: newEmployeeEntries.map(e => ({
          centerId: e.centerId,
          centerName: e.centerName,
          role: e.role,
          workerType: e.workerType,
          blindStatus: e.blindStatus
        }))
      });

      // Mirror the same archive-instead-of-delete handling on the employee side.
      // originalCenterIds already defined above, reuse it
      const isEditedProjectRow = proj =>
        proj.id === project.id && originalCenterIds.includes(proj.centerId);

      const untouchedProjects = currentEmployeeData.projects.filter(proj => !isEditedProjectRow(proj));
      const editedProjectRows = currentEmployeeData.projects.filter(isEditedProjectRow);

      const archivedProjectRows = editedProjectRows
        .filter(proj => archivedCenterIds.has(proj.centerId))
        .map(proj => archiveAssignment(proj, unassignedAtIso));

      const previousProjectRowFor = (centerId, role) =>
        editedProjectRows.find(proj => proj.centerId === centerId && proj.role === role) ||
        editedProjectRows.find(proj => proj.centerId === centerId);

      const newProjectEntries = [];
      for (const role of selectedRole) {
        for (const centerId of selectedCenters) {
          let selectedTariff;
          if (useDefaultTestTariff && effectiveCenterTariffs[centerId] === 'test-tariff-default') {
            selectedTariff = defaultTestTariff;
          } else {
            selectedTariff = tariffs.find(tariff => tariff.id === effectiveCenterTariffs[centerId]);
          }
          const rate = selectedTariff ? {
            id: selectedTariff.id,
            type: selectedTariff.type,
            value: selectedTariff.value,
            price: selectedTariff.price,
            currency: selectedTariff.currency
          } : (existingRateByCenter[centerId] || null);
          const previousProjectRow = restoreAssignment(previousProjectRowFor(centerId, role)) || {};
          newProjectEntries.push({
            ...previousProjectRow,
            id: project.id,
            titleShort: project.titleShort,
            titleLong: project.titleLong,
            projectDate: project.projectDate,
            rate,
            role: role,
            centerId: centerId,
            centerName: centers.find(c => c.id === centerId)?.Adı,
            assignedAt: workerToEdit.assignedAt || new Date(),
            workerType: selectedWorkerType,
            blindStatus: selectedBlindStatus,
            status: selectedStatus
          });
        }
      }

      const finalProjects = [...untouchedProjects, ...archivedProjectRows, ...newProjectEntries];

      console.log('🔄 Updating project in employee:', {
        employeeId: workerToEdit.id,
        employeeName: `${workerToEdit.name || workerToEdit.eName} ${workerToEdit.surname || workerToEdit.eSurname}`,
        projectId: project.id,
        projectTitle: project.titleShort || project.titleLong,
        selectedWorkerType,
        selectedBlindStatus,
        newProjectEntries: newProjectEntries.map(p => ({
          centerId: p.centerId,
          centerName: p.centerName,
          role: p.role,
          workerType: p.workerType,
          blindStatus: p.blindStatus
        }))
      });

      // Update both documents
      await updateDoc(projectRef, {
        employees: finalEmployees
      });

      await updateDoc(employeeRef, {
        projects: finalProjects
      });

      toast.success(toastText('updated'));
      setShowEditWorkerModal(false);
      await fetchProjectWorkers(); // Refresh the workers list

      // Reset form
      resetForm();
      setWorkerToEdit(null);

    } catch (error) {
      console.error('Error updating worker:', error);
      toast.error(toastText('updateError'));
    }
    setLoading(false);
  };

  /**
   * Çalışanı projeden tamamen çıkarır: pasife almak veya "ayrıldı" damgası
   * vurmak yerine, iki taraftaki atama satırlarını da siler —
   * Projects/{id}.employees ve Employees/{id}.projects.
   * Geri alınamaz; bu yüzden yalnızca ana admin çalıştırabilir.
   */
  const handleRemoveWorkerCompletely = async () => {
    if (!isMainAdmin || !workerToRemove || removing) return;

    setRemoving(true);
    try {
      const projectRef = doc(db, 'Projects', project.id);
      const employeeRef = doc(db, 'Employees', workerToRemove.id);

      const [projectDoc, employeeDoc] = await Promise.all([
        getDoc(projectRef),
        getDoc(employeeRef),
      ]);

      if (!projectDoc.exists()) {
        toast.error(toastText('projectMissing'));
        setRemoving(false);
        return;
      }

      const projectData = projectDoc.data() || {};
      // Ayrılmış (arşiv) satırlar dahil, bu çalışana ait TÜM satırlar gider.
      const remainingEmployees = (projectData.employees || []).filter(
        (emp) => emp.id !== workerToRemove.id
      );

      await updateDoc(projectRef, { employees: remainingEmployees });

      // Çalışan dokümanı silinmiş olabilir; varsa aynası da temizlenir.
      if (employeeDoc.exists()) {
        const employeeData = employeeDoc.data() || {};
        const remainingProjects = (employeeData.projects || []).filter(
          (proj) => proj.id !== project.id
        );
        await updateDoc(employeeRef, { projects: remainingProjects });
      }

      toast.success(
        toastText('removed', { name: `${workerToRemove.name || ''} ${workerToRemove.surname || ''}`.trim() })
      );
      setWorkerToRemove(null);
      setSelectedRowId(null);
      await fetchProjectWorkers();
    } catch (error) {
      console.error('Error removing worker from project:', error);
      toast.error(toastText('removeError'));
    }
    setRemoving(false);
  };

  // Satır düzeyindeki sütun filtreleri (eskisi gibi; Excel de bu satırları alır)
  const filterWorkers = (rows) => rows.filter(worker => (
    (!filters.name || lower(worker.name || worker.eName).includes(lower(filters.name))) &&
    (!filters.surname || lower(worker.surname || worker.eSurname).includes(lower(filters.surname))) &&
    (!filters.role || worker.role === filters.role) &&
    (!filters.rate || lower(worker.rate?.type).includes(lower(filters.rate))) &&
    (!filters.center || lower(worker.centerName).includes(lower(filters.center))) &&
    (!filters.info || lower(worker.info).includes(lower(filters.info))) &&
    (!filters.assignedAt ||
      formatDate(worker.assignedAt).includes(filters.assignedAt) ||
      formatAssignedDate(worker.assignedAt, locale).includes(filters.assignedAt))
  ));

  const groupWorkersByEmployee = (rows) => {
    const grouped = {};
    rows.forEach(worker => {
      const key = worker.id;
      if (!grouped[key]) {
        grouped[key] = {
          id: worker.id,
          name: worker.name || worker.eName,
          surname: worker.surname || worker.eSurname,
          assignedAt: worker.assignedAt,
          centers: []
        };
      }
      grouped[key].centers.push({
        centerId: worker.centerId,
        centerName: worker.centerName,
        role: worker.role,
        rate: worker.rate,
        info: worker.info,
        status: worker.status || 'active',
        workerType: worker.workerType, // Primer/Backup bilgisi
        blindStatus: worker.blindStatus, // Blinded/Unblinded bilgisi
        assignedAt: worker.assignedAt, // Tarih bilgisi (sıralama için)
        unassignedAt: worker.unassignedAt // Ayrıldıysa ayrılma tarihi
      });
    });
    return Object.values(grouped);
  };

  // Gruplar her zaman çalışanın TÜM satırlarından kurulur: bir filtre açıkken
  // düzenlemek, filtre dışında kalan rol / merkez satırlarını silmesin.
  const allRows = useMemo(
    () => groupWorkersByEmployee(workers).map(worker => ({ worker, summary: summarizeWorker(worker) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workers]
  );
  const matchedIds = new Set(filterWorkers(workers).map(worker => worker.id));
  const search = lower(searchTerm.trim());
  const visibleRows = allRows.filter(({ worker, summary }) => (
    matchedIds.has(worker.id) &&
    (!filters.status || summary.status === filters.status) &&
    (!search || lower(`${worker.name || ''} ${worker.surname || ''}`).includes(search))
  ));
  const activeFilterCount = WORKER_FILTER_KEYS.filter(key => filters[key]).length;
  const filtersActive = activeFilterCount > 0 || Boolean(searchTerm.trim());
  const selectedRow = allRows.find(({ worker }) => worker.id === selectedRowId) || null;

  const stats = [
    { key: 'total', value: allRows.length, icon: LuUsers, tone: 'tw-text-slate-900 dark:tw-text-white' },
    { key: 'active', value: allRows.filter(row => row.summary.status === 'active').length, icon: LuUserCheck, tone: 'tw-text-emerald-700 dark:tw-text-emerald-300' },
    { key: 'passive', value: allRows.filter(row => row.summary.status === 'passive').length, icon: LuUserMinus, tone: 'tw-text-slate-600 dark:tw-text-slate-300' },
    { key: 'left', value: allRows.filter(row => row.summary.status === 'left').length, icon: LuUserRoundX, tone: 'tw-text-amber-700 dark:tw-text-amber-300' },
  ];

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchTerm('');
  };

  const downloadExcel = () => {
    const visibleIds = new Set(visibleRows.map(({ worker }) => worker.id));
    const dataToExport = filterWorkers(workers).filter(worker => visibleIds.has(worker.id)).map(worker => ({
      'Çalışan Adı': worker.name || worker.eName || '',
      'Çalışan Soyadı': worker.surname || worker.eSurname || '',
      'Rol': worker.role || '',
      'Tarife': worker.rate ? `${worker.rate.type} - ${worker.rate.value}` : '',
      'Merkez': worker.centerName || '',
      'Bilgi': worker.info || '',
      'Atanma Tarihi': formatDate(worker.assignedAt),
      'Ayrılma Tarihi': formatUnassignedDate(worker),
      'Durum': isArchivedAssignment(worker) ? 'Ayrıldı' : (worker.status === 'passive' ? 'Pasif' : 'Aktif')
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Çalışanlar');
    XLSX.writeFile(workbook, `${project.titleShort}_Çalışan_Listesi.xlsx`);
  };

  const openEditWorker = (row) => {
    setWorkerToEdit(row);
    const tariffsMap = {};
    row.centers.forEach(center => {
      tariffsMap[center.centerId] = center.rate?.id || '';
    });
    // Collect all unique roles from all centers
    const uniqueRoles = [...new Set(row.centers.map(c => c.role).filter(Boolean))];
    setSelectedRole(uniqueRoles);
    // Collect unique center IDs (remove duplicates). Centers the
    // worker already left are listed separately so they are not
    // re-created as active assignments on save.
    const archivedRows = row.centers.filter(isArchivedAssignment);
    const activeCenterIds = new Set(
      row.centers.filter(c => !isArchivedAssignment(c)).map(c => c.centerId)
    );
    const uniqueArchivedCenters = [];
    archivedRows.forEach(c => {
      // A center counts as left only when no active row remains for it
      // (the worker may hold two roles and have left just one).
      if (activeCenterIds.has(c.centerId)) return;
      if (uniqueArchivedCenters.some(a => a.centerId === c.centerId)) return;
      uniqueArchivedCenters.push({
        centerId: c.centerId,
        centerName: c.centerName,
        unassignedAt: c.unassignedAt
      });
    });
    setSelectedCenters([...activeCenterIds]);
    setArchivedCenters(uniqueArchivedCenters);
    setCenterTariffs(tariffsMap);
    setSelectedInfo(row.centers[0]?.info || '');

    // workerType ve blindStatus: en son güncellenen merkezdeki değer (assignedAt'e göre)
    const sortedCenters = [...row.centers].sort((a, b) => {
      const dateA = a.assignedAt?.toDate?.() || a.assignedAt || new Date(0);
      const dateB = b.assignedAt?.toDate?.() || b.assignedAt || new Date(0);
      return dateB - dateA; // En yeni önce
    });

    setSelectedWorkerType(sortedCenters[0]?.workerType || '');
    setSelectedBlindStatus(sortedCenters[0]?.blindStatus || '');
    setSelectedStatus(row.centers[0]?.status || 'active');
    setShowEditWorkerModal(true);
  };

  const workerActions = (stretch) => selectedRow && (
    <WorkerActions
      stretch={stretch}
      onEdit={() => openEditWorker(selectedRow.worker)}
      onUpload={() => {
        setSelectedEmployeeForUpload(selectedRow.worker);
        setShowEmployeeFileUploadModal(true);
      }}
      onRemove={() => setWorkerToRemove(selectedRow.worker)}
      canRemove={isMainAdmin}
    />
  );

  const list = (
    <WorkerList
      rows={visibleRows}
      wide={wide}
      loading={loading}
      totalCount={allRows.length}
      selectedId={selectedRowId}
      onSelect={setSelectedRowId}
      filters={filters}
      onFilterChange={setFilter}
      filtersActive={filtersActive}
      onClearFilters={clearFilters}
      onAdd={() => setShowAddWorkerModal(true)}
    />
  );

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        size="xl"
        title={t('projectManagement.workers.title')}
        subtitle={project.titleShort || project.customer}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30" size="xs">
            <LuUsers aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        actions={(
          <>
            <Button size="sm" onClick={downloadExcel} disabled={loading || workers.length === 0}>
              <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
              {t('projectManagement.workers.export')}
            </Button>
            <Button variant="primary" size="sm" onClick={() => setShowAddWorkerModal(true)} className="max-sm:tw-flex-1">
              <LuUserPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
              {t('projectManagement.workers.add')}
            </Button>
          </>
        )}
        footer={<Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>}
      >
        <div className="tw-space-y-4">
          {/* Summary */}
          <dl aria-label={t('projectManagement.workers.summary')} className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.key} className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-py-2.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                  <Icon aria-hidden="true" className={`tw-h-5 tw-w-5 tw-shrink-0 ${stat.tone}`} />
                  <div className="tw-min-w-0">
                    <dd className={`tw-text-lg tw-font-semibold tw-leading-none tw-tabular-nums ${stat.tone}`}>
                      {loading ? <span aria-hidden="true" className="tw-block tw-h-5 tw-w-6 tw-animate-pulse tw-rounded tw-bg-slate-200/80 dark:tw-bg-white/10" /> : stat.value.toLocaleString(locale)}
                    </dd>
                    <dt className="tw-mt-1 tw-truncate tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{t(`projectManagement.workers.stats.${stat.key}`)}</dt>
                  </div>
                </div>
              );
            })}
          </dl>

          {/* Search and filters */}
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-relative tw-min-w-0 tw-flex-1">
              <LuSearch aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={t('projectManagement.workers.searchPlaceholder')}
                aria-label={t('projectManagement.workers.searchPlaceholder')}
                className={`${GLASS_FIELD} tw-pl-10 tw-pr-10`}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  aria-label={t('dataTable.search.clear')}
                  className="tw-absolute tw-right-1.5 tw-top-1/2 tw-grid tw-h-7 tw-w-7 -tw-translate-y-1/2 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
                >
                  <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
                </button>
              )}
            </div>
            {!wide && (
              <Button onClick={() => setFilterSheetOpen(true)} className={activeFilterCount > 0 ? 'tw-text-teal-700 dark:tw-text-teal-300' : ''}>
                <LuListFilter aria-hidden="true" className="tw-h-4 tw-w-4" />
                {activeFilterCount > 0 ? `${t('dataTable.filters')} · ${activeFilterCount}` : t('dataTable.filters')}
              </Button>
            )}
            {wide && filtersActive && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <LuX aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                {t('dataTable.clearAll')}
              </Button>
            )}
          </div>

          {docked ? (
            <div className="tw-grid tw-items-start tw-gap-4 tw-grid-cols-[minmax(0,1fr)_20rem]">
              {list}
              <aside
                aria-label={t('projectManagement.workers.detail.title')}
                className="tw-sticky tw-top-0 tw-max-h-[min(34rem,58vh)] tw-overflow-y-auto tw-overscroll-contain tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50/70 tw-p-4 glass-scrollbar dark:tw-border-white/10 dark:tw-bg-white/[0.02]"
              >
                {selectedRow ? (
                  <div className="tw-space-y-4">
                    <WorkerDetailCard worker={selectedRow.worker} summary={selectedRow.summary} showHeader />
                    <div className="tw-flex tw-flex-wrap tw-gap-2 tw-border-t tw-border-slate-200 tw-pt-4 dark:tw-border-white/10">
                      {workerActions(true)}
                    </div>
                  </div>
                ) : (
                  <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-px-4 tw-py-10 tw-text-center">
                    <span className="tw-grid tw-h-12 tw-w-12 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05] dark:tw-text-slate-500">
                      <LuMousePointerClick aria-hidden="true" className="tw-h-6 tw-w-6" />
                    </span>
                    <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{t('projectManagement.workers.selectHint')}</p>
                  </div>
                )}
              </aside>
            </div>
          ) : list}

          {!loading && allRows.length > 0 && (
            <p className="tw-text-right tw-text-xs tw-text-slate-500 tw-tabular-nums dark:tw-text-slate-400">
              {t('projectManagement.workers.showing', { shown: visibleRows.length, total: allRows.length })}
            </p>
          )}
        </div>
      </Modal>

      {/* Ayrıntı: dar ekranda ayrı bir pencerede */}
      <Modal
        isOpen={!docked && Boolean(selectedRow)}
        onClose={() => setSelectedRowId(null)}
        size="md"
        title={selectedRow ? `${selectedRow.worker.name || ''} ${selectedRow.worker.surname || ''}`.trim() : ''}
        subtitle={t('projectManagement.workers.detail.title')}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30" size="xs">
            <LuUsers aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={workerActions(false)}
      >
        {selectedRow && <WorkerDetailCard worker={selectedRow.worker} summary={selectedRow.summary} />}
      </Modal>

      <FilterSheet
        isOpen={filterSheetOpen && !wide}
        onClose={() => setFilterSheetOpen(false)}
        fields={workerFilterFields(t)}
        filters={filters}
        getFilterState={(key, value) => (value ? 'applied' : 'off')}
        onFilterChange={setFilter}
        onClearAll={() => setFilters(EMPTY_FILTERS)}
        resultCount={visibleRows.length}
      />

      {showAddWorkerModal && (
        <WorkerFormModal
          mode="add"
          onClose={() => {
            setShowAddWorkerModal(false);
            resetForm();
          }}
          onSubmit={handleAddWorker}
          loading={loading}
          availableEmployees={availableEmployees}
          centers={centers}
          selectedEmployee={selectedEmployee}
          setSelectedEmployee={setSelectedEmployee}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          selectedCenters={selectedCenters}
          setSelectedCenters={setSelectedCenters}
          selectedInfo={selectedInfo}
          setSelectedInfo={setSelectedInfo}
          selectedWorkerType={selectedWorkerType}
          setSelectedWorkerType={setSelectedWorkerType}
          selectedBlindStatus={selectedBlindStatus}
          setSelectedBlindStatus={setSelectedBlindStatus}
          hasManagerWarning={hasManagerWarning}
        />
      )}
      {showEditWorkerModal && workerToEdit && (
        <WorkerFormModal
          mode="edit"
          worker={workerToEdit}
          onClose={() => {
            setShowEditWorkerModal(false);
            setWorkerToEdit(null);
            resetForm();
          }}
          onSubmit={handleEditWorker}
          loading={loading}
          centers={centers}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          selectedCenters={selectedCenters}
          setSelectedCenters={setSelectedCenters}
          archivedCenters={archivedCenters}
          setArchivedCenters={setArchivedCenters}
          selectedInfo={selectedInfo}
          setSelectedInfo={setSelectedInfo}
          selectedWorkerType={selectedWorkerType}
          setSelectedWorkerType={setSelectedWorkerType}
          selectedBlindStatus={selectedBlindStatus}
          setSelectedBlindStatus={setSelectedBlindStatus}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          hasManagerWarning={hasManagerWarning}
        />
      )}
      {showEmployeeFileUploadModal && selectedEmployeeForUpload && (
        <EmployeeFileUploadModal
          project={project}
          employee={selectedEmployeeForUpload}
          onClose={() => {
            setShowEmployeeFileUploadModal(false);
            setSelectedEmployeeForUpload(null);
          }}
        />
      )}
      {/* Kalıcı silme onayı — yalnızca ana admin bu noktaya ulaşabilir */}
      {isMainAdmin && (
        <WorkerRemoveDialog
          worker={workerToRemove}
          projectTitle={project.titleShort}
          removing={removing}
          onConfirm={handleRemoveWorkerCompletely}
          onCancel={() => setWorkerToRemove(null)}
        />
      )}
    </>
  );
};

WorkersModal.propTypes = {
  project: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default WorkersModal;
